import { CdkConnectedOverlay, ConnectedPosition, Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    signal,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DateTime } from 'luxon';
import { DateInputMaskDirective } from '../../directives/date-input-mask.directive';
import { IdGeneratorPipe } from '../../pipes/id-generator.pipe';
import { DateTimeService } from '../../services';
import { IconSanitizerService } from '../../services/icon-sanitizer.service';
import { CDK_IMPORTS } from '../../shared/standalone-imports';
import { formatTimeForBrowser } from '../../utils/date-utils';
import { CalendarType } from '../calendar/calendar.component';
import { DatepickerComponent } from '../datepicker/datepicker.component';
import { CalendarRangesConfig } from '../datepicker/default-ranges';
import { BaseFilterComponent } from '../filters/base-filter/base-filter.component';

@Component({
    selector: 'lib-datepicker-dropdown',
    templateUrl: './datepicker-dropdown.component.html',
    styleUrls: ['./datepicker-dropdown.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule, IdGeneratorPipe, FormsModule, DateInputMaskDirective, DatepickerComponent, ...CDK_IMPORTS]
})
export class DatepickerDropdownComponent extends BaseFilterComponent implements OnInit, OnChanges {
    private readonly iconSanitizer = inject(IconSanitizerService);
    private readonly overlay = inject(Overlay);

    @ViewChild('datepicker')
    datepicker!: DatepickerComponent;
    @ViewChild('dateInput')
    dateInput!: ElementRef<HTMLInputElement>;
    @ViewChild('container')
    container!: ElementRef<HTMLInputElement>;
    @ViewChild('overlayPanel')
    overlayPanel!: ElementRef<HTMLElement>;

    @Input()
    defaultTitle?: string;

    @Input()
    error: boolean;

    @Input()
    dateRangeEnabled: boolean;

    @Input()
    rangeDirection: 'backward' | 'forward' | 'both' = 'backward';

    @Input()
    customDefaultRanges: CalendarRangesConfig[] | null = null;

    @Input()
    timeEnabled: boolean;

    @Input()
    disabled: boolean;

    @Input()
    selectedDate?: Date;

    @Input()
    datepickerDropdownId?: string;

    @Output()
    selectedDateChange: EventEmitter<Date | undefined>;

    @Input()
    dateFrom?: Date;

    @Output()
    dateFromChange: EventEmitter<Date | undefined>;

    @Input()
    dateTo?: Date;

    @Output()
    dateToChange: EventEmitter<Date | undefined>;

    @Input()
    minDate?: Date;

    @Input()
    isReadOnly?: boolean;

    @Input()
    flexibleSize: boolean;

    @Input()
    displaySelected: boolean;

    @Input()
    calendarTypeDisplay: CalendarType;

    @Input()
    isMonthView?: boolean;

    @Input()
    format: string;

    @Input()
    allowSelectEmptyDate?: boolean;

    @Input()
    emptyDateSelected?: boolean;

    @Output()
    emptyDateSelectedChange: EventEmitter<boolean>;

    @Output()
    override filterChanged: EventEmitter<void>;

    @Output()
    calendarTypeDisplayChange: EventEmitter<CalendarType> = new EventEmitter<CalendarType>();

    selectedDateTime?: DateTime;

    dateTimeFrom?: DateTime;

    dateTimeTo?: DateTime;

    minDateTime?: DateTime;

    opened: boolean;

    title?: string;

    originalValues?: { selectedDate?: Date; dateFrom?: Date; dateTo?: Date };

    readonly calendarIcon = this.iconSanitizer.getIcon('dropdownCalendar');

    keyboardDate: string;
    placeholderText?: string;
    inputText?: string;
    showInputSearch = signal(false);
    overlayWidth = signal(0);
    scrollStrategy: ScrollStrategy = this.overlay.scrollStrategies.close();

    calendarType: typeof CalendarType;

    //CDK overlay positions depending ShowInputSearch.
    readonly overlayPositions = computed<ConnectedPosition[]>(() => {
        if (this.showInputSearch()) {
            return [
                { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top' },
                { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'bottom' }
            ];
        }

        // CDK default: open below, fallback above.
        return [
            { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
            { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
            { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
            { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' }
        ];
    });

    constructor(private readonly dateTimeService: DateTimeService) {
        super();
        this.error = false;
        this.dateRangeEnabled = false;
        this.timeEnabled = false;
        this.disabled = false;
        this.opened = false;
        this.isReadOnly = false;
        this.flexibleSize = false;
        this.displaySelected = false;
        this.calendarTypeDisplay = CalendarType.MONTH;
        this.selectedDateChange = new EventEmitter<Date | undefined>();
        this.dateFromChange = new EventEmitter<Date | undefined>();
        this.dateToChange = new EventEmitter<Date | undefined>();
        this.filterChanged = new EventEmitter<void>();
        this.emptyDateSelectedChange = new EventEmitter<boolean>();
        this.keyboardDate = '';
        this.format = 'MM/dd/yyyy';
        this.calendarType = CalendarType;
    }

    ngOnInit(): void {
        this.setTitle();
        this.showInputSearch.set(!this.dateRangeEnabled && this.calendarTypeDisplay === 'Month');
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectedDate'] && !this.dateRangeEnabled) {
            this.selectedDateTime = this.selectedDate
                ? this.dateTimeService.getDateTimeFromString(this.selectedDate.toString())
                : undefined;
            this.setKeyboardDate();
        }

        if (changes['defaultTitle']?.currentValue) {
            this.title = changes['defaultTitle'].currentValue;
        }

        if (changes['disabled']?.currentValue) {
            this.selectedDate = this.dateFrom = this.dateTo = undefined;
        }

        if (changes['selectedDate']) {
            this.selectedDateTime = this.selectedDate
                ? this.dateTimeService.getDateTimeFromString(this.selectedDate.toString())
                : undefined;
        }

        if (changes['dateFrom']) {
            this.dateTimeFrom = this.dateFrom
                ? this.dateTimeService.getDateTimeFromString(this.dateFrom.toString())
                : undefined;
        }

        if (changes['dateTo']) {
            this.dateTimeTo = this.dateTo
                ? this.dateTimeService.getDateTimeFromString(this.dateTo.toString())
                : undefined;
        }

        if (!changes['dateFrom']?.currentValue && !changes['dateTo']?.currentValue) {
            this.title = this.defaultTitle ?? '';
        }

        if (changes['minDate']) {
            this.minDateTime = this.minDate
                ? this.dateTimeService.getDateTimeFromString(this.minDate.toString())
                : undefined;
        }

        if (changes['dateFrom']?.currentValue || changes['dateTo']?.currentValue) {
            this.setTitle();
        }

        if (changes['selectedDate'] && !this.dateRangeEnabled) {
            this.setTitle();
        }
    }

    onOverlayAttach(overlay: CdkConnectedOverlay) {
        this.overlayWidth.set(this.container.nativeElement.offsetWidth);
        setTimeout(() => {
            overlay.overlayRef.updatePosition();

            this.setKeyboardDate();

            if (this.showInputSearch() && this.dateInput) {
                this.dateInput.nativeElement.focus();
            }
        });
    }

    openClose(): void {
        if (this.disabled || this.isReadOnly) return;

        this.overlayWidth.set(this.container.nativeElement.offsetWidth);

        if (this.opened) {
            this.close();
            return;
        }

        if (!this.opened) {
            this.originalValues = {
                selectedDate: this.selectedDate,
                dateFrom: this.dateFrom,
                dateTo: this.dateTo
            };
            this.setKeyboardDate();
            if (this.isMonthView) {
                this.calendarTypeDisplayChange.emit(this.calendarType.YEAR);
            } else {
                this.calendarTypeDisplayChange.emit(this.calendarType.MONTH);
            }
        }

        this.opened = !this.opened;
    }

    onHeaderKeydown(e: KeyboardEvent): void {
        if (this.opened && this.showInputSearch()) {
            return;
        }

        if (this.opened && e.key === 'Tab') {
            this.onTabPressed(e);
            return;
        }

        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.openClose();
        }
    }

    onChanged(): void {
        this.selectedDate = this.selectedDateTime
            ? this.dateTimeService.formatDateTimeToDate(this.selectedDateTime)
            : undefined;
        this.dateFrom = this.dateTimeFrom ? this.dateTimeService.formatDateTimeToDate(this.dateTimeFrom) : undefined;
        this.dateTo = this.dateTimeTo ? this.dateTimeService.formatDateTimeToDate(this.dateTimeTo) : undefined;
        this.setKeyboardDate();

        this.selectedDateChange.emit(this.selectedDate);
        this.dateFromChange.emit(this.dateFrom);
        this.dateToChange.emit(this.dateTo);

        const selectedDateHasChanged =
            typeof this.originalValues?.selectedDate === typeof Date
                ? this.originalValues?.selectedDate?.toISOString() !== this.selectedDate?.toISOString()
                : this.originalValues?.selectedDate !== this.selectedDate;
        const dateFromHasChanged =
            typeof this.originalValues?.dateFrom === typeof Date
                ? this.originalValues?.dateFrom?.toISOString() !== this.dateFrom?.toISOString()
                : this.originalValues?.dateFrom !== this.dateFrom;
        const dateToHasChanged =
            typeof this.originalValues?.dateTo === typeof Date
                ? this.originalValues?.dateTo?.toISOString() !== this.dateTo?.toISOString()
                : this.originalValues?.dateTo !== this.dateTo;

        if (selectedDateHasChanged || dateFromHasChanged || dateToHasChanged) {
            this.filterChanged.emit();
        }

        this.openClose();
    }

    onTitleChanged(value: string) {
        setTimeout(() => {
            this.title = value;
            this.setKeyboardDate();
        }, 0);
    }

    onKeyboardDateChange(event: string): void {
        this.keyboardDate = event;
    }

    onKeyboardDateBlur(event: boolean): void {
        if (!this.keyboardDate) {
            this.clearDate();
            this.setTitle();
            return;
        }

        if (!event) {
            this.setKeyboardDate();
        }
    }

    setKeyboardDate(): void {
        this.keyboardDate = this.selectedDate
            ? `${DateTime.fromJSDate(this.selectedDate).toFormat(this.format)}`.trim()
            : '';
    }

    onCalendarTypeDisplayChange(calendarType: CalendarType): void {
        this.calendarTypeDisplayChange.emit(calendarType);
    }

    onEmptyDateSelectedChange(val: boolean) {
        this.emptyDateSelected = val;
        this.emptyDateSelectedChange.emit(val);
    }

    onEnterPressed(event: Event | KeyboardEvent): void {
        const keyboardEvent = event as KeyboardEvent;
        keyboardEvent.preventDefault();
        keyboardEvent.stopPropagation();

        if (!this.keyboardDate) {
            this.clearDate();
            this.setTitle();
            this.close();
            return;
        }

        const inputDateTime = this.dateTimeService.getDateTimeFromString(this.keyboardDate);

        if (!inputDateTime || !inputDateTime.isValid) {
            this.setKeyboardDate();
            return;
        }

        if (this.dateRangeEnabled) {
            if (!this.dateFrom) {
                this.dateTimeFrom = inputDateTime;
                this.dateFrom = this.dateTimeService.formatDateTimeToDate(inputDateTime);
                this.dateFromChange.emit(this.dateFrom);
            } else if (!this.dateTo) {
                this.dateTimeTo = inputDateTime;
                this.dateTo = this.dateTimeService.formatDateTimeToDate(inputDateTime);
                this.dateToChange.emit(this.dateTo);
            }
        } else {
            this.selectedDateTime =
                this.timeEnabled && this.selectedDateTime
                    ? this.selectedDateTime.set({
                          year: inputDateTime.year,
                          month: inputDateTime.month,
                          day: inputDateTime.day
                      })
                    : inputDateTime;
            this.selectedDate = this.dateTimeService.formatDateTimeToDate(this.selectedDateTime);
            this.selectedDateChange.emit(this.selectedDate);
        }

        this.setKeyboardDate();
        this.setTitle();
        this.filterChanged.emit();
        this.close();
    }

    onEscPressed(event: Event | KeyboardEvent): void {
        const keyboardEvent = event as KeyboardEvent;

        if (keyboardEvent.key === 'Escape') {
            keyboardEvent.preventDefault();
            this.close();
        }
    }

    onTabPressed(event: Event): void {
        const keyboardEvent = event as KeyboardEvent;

        if (keyboardEvent.shiftKey) {
            return;
        }

        keyboardEvent.preventDefault();
        keyboardEvent.stopPropagation();
        setTimeout(() => this.overlayPanel?.nativeElement.focus());
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        this.overlayWidth.set(this.container?.nativeElement.offsetWidth ?? 0);
    }

    private setTitle(): void {
        if (this.dateRangeEnabled) {
            const dateFrom = this.dateTimeFrom?.toFormat(this.format);
            const dateTo = this.dateTimeTo?.toFormat(this.format);

            if (dateFrom && dateTo) {
                this.title = `${dateFrom} - ${dateTo}`;
            } else if (dateFrom) {
                this.title = dateFrom;
            } else if (dateTo) {
                this.title = dateTo;
            }
        } else if (this.selectedDate) {
            const timeValueText = this.timeEnabled ? `- ${formatTimeForBrowser(this.selectedDate)}` : '';

            const formattedDate = DateTime.fromJSDate(this.selectedDate).toFormat(this.format);

            this.title = `${formattedDate} ${timeValueText}`;

            if (!this.timeEnabled) {
                this.setKeyboardDate();
            }
        }
    }

    private close(): void {
        this.opened = false;
        this.container?.nativeElement.focus();
    }

    private clearDate(): void {
        this.selectedDate = undefined;
        this.selectedDateTime = undefined;
        this.keyboardDate = '';

        this.selectedDateChange.emit(undefined);
    }
}
